import React, { useState, useEffect } from "react";
import useFocusTrap from "../../hooks/useFocusTrap";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import CodeBlock from "@tiptap/extension-code-block"; 
import Underline from "@tiptap/extension-underline"; 
import TextAlign from "@tiptap/extension-text-align"; 
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript"; 
import Superscript from "@tiptap/extension-superscript"; 
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { useTheme } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import ImageModal from "./ImageModal";
import {
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Underline as UnderlineIcon,
  Code2,
  SquareCode,
  Link as LinkIcon,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  ChevronDown,
  Bold,
  Italic,
  ImagePlus as ImageIcon,
} from "lucide-react";

const cleanEditorHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return html;
  }
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  const imageContainers = tempDiv.querySelectorAll('.image-container block');
  imageContainers.forEach(container => {
    const editOverlay = container.querySelector('.image-edit-overlay');
    if (editOverlay) {
      editOverlay.remove();
    }
    
    const errorDiv = container.querySelector('.image-error');
    if (errorDiv) {
      errorDiv.remove();
    }
    
    const img = container.querySelector('img');
    if (img) {
      const cleanImg = img.cloneNode(true);
      cleanImg.removeAttribute('onclick');
      cleanImg.removeAttribute('data-editor');
      container.parentNode.replaceChild(cleanImg, container);
    }
  });
  
  const standaloneOverlays = tempDiv.querySelectorAll('.image-edit-overlay, .image-error');
  standaloneOverlays.forEach(element => element.remove());
  
  return tempDiv.innerHTML;
};

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: element => element.getAttribute('src'),
        renderHTML: attributes => {
          if (!attributes.src) {
            return {}
          }
          return {
            src: attributes.src,
            onError: "this.style.display='none'; this.nextElementSibling.style.display='block';",
          }
        },
      },
      alignment: {
        default: 'center',
        parseHTML: element => {
          const container = element.closest('.image-container');
          if (container) {
            if (container.classList.contains('justify-left')) return 'left';
            if (container.classList.contains('justify-center')) return 'center';
            if (container.classList.contains('justify-right')) return 'right';
          }
          return 'center';
        },
        renderHTML: attributes => {
          return {}
        },
      },
      fileName: {
        default: null,
        parseHTML: element => {
          const container = element.closest('.image-container');
          if (container) {
            return container.getAttribute('data-file-name') || null;
          }
          return null;
        },
        renderHTML: attributes => {
          return {}
        },
      },
    }
  },
  
  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: element => {
          const src = element.getAttribute('src');
          const container = element.closest('.image-container');
          let alignment = 'center';
          let fileName = null;
          if (container) {
            if (container.classList.contains('justify-left')) alignment = 'left';
            else if (container.classList.contains('justify-center')) alignment = 'center';
            else if (container.classList.contains('justify-right')) alignment = 'right';
            fileName = container.getAttribute('data-file-name') || null;
          }
          return { src, alignment, fileName }
        },
      },
      {
        tag: 'div.image-container',
        getAttrs: element => {
          const img = element.querySelector('img');
          if (!img) return false;
          const src = img.getAttribute('src');
          let alignment = 'center';
          if (element.classList.contains('justify-left')) alignment = 'left';
          else if (element.classList.contains('justify-center')) alignment = 'center';
          else if (element.classList.contains('justify-right')) alignment = 'right';
          const fileName = element.getAttribute('data-file-name') || null;
          return { src, alignment, fileName }
        },
      },
    ]
  },
  
  renderHTML({ HTMLAttributes, node }) {
    const alignment = node.attrs.alignment || 'center';
    const alignmentClass = `justify-${alignment}`;
    const fileName = node.attrs.fileName || null;
    return [
      'div',
      { 
        class: `image-container ${alignmentClass}`, 
        style: 'position: relative; display: inline-block; cursor: pointer;',
        onClick: `window.editImage && window.editImage(${JSON.stringify(node.attrs)})`,
        ...(fileName ? { 'data-file-name': fileName } : {}),
      },
      [
        'img',
        {
          ...HTMLAttributes,
          style: 'max-width: 100%; height: auto; border-radius: 4px;',
        }
      ],
      [
        'div',
        {
          class: 'image-edit-overlay',
          style: 'position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: none; align-items: center; justify-content: center; border-radius: 4px;',
        },
        [
          'span',
          {
            style: 'color: white; font-size: 12px; background: rgba(0,0,0,0.7); padding: 4px 8px; border-radius: 4px;',
          },
          'Click to edit'
        ]
      ]
    ]
  },
});

const TiptapEditor = ({
  content = "<p>Add your Standardized template content here...</p>",
  editable = true,
  onUpdate,
  onSave,
  showToolbar = true,
  placeholder = "Add your Standardized template content here...",
}) => {
  const theme = useTheme();
  const [editorState, setEditorState] = useState({});
  const [showHeadingMenu, setShowHeadingMenu] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [imageAlignment, setImageAlignment] = useState("center");
  const [showEditImageModal, setShowEditImageModal] = useState(false);
  const [editingImageUrl, setEditingImageUrl] = useState("");
  const [editingImageFile, setEditingImageFile] = useState(null);
  const [editingImagePreview, setEditingImagePreview] = useState(null);
  const [editingImagePreviewError, setEditingImagePreviewError] = useState(false);
  const [editingImageAlignment, setEditingImageAlignment] = useState("center");
  const [selectedImageNode, setSelectedImageNode] = useState(null);

  const handleLinkCancel = () => {
    setIsLinkModalOpen(false);
    setLinkText("");
    setLinkUrl("");
  };

  const linkModalRef = useFocusTrap(isLinkModalOpen, handleLinkCancel);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      // Additional extensions not included in StarterKit
      CodeBlock,
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-500 underline",
        },
      }),
      Subscript,
      Superscript,
      CustomImage.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto",
        },
        allowBase64: true,
      }),
      Placeholder.configure({
        placeholder: placeholder,
      }),
    ],
    content: content,
    editable: editable,
    editorProps: {
      attributes: {
        class: "focus:outline-none min-h-[400px] p-4",
        style: "line-height: 1.6;",
      },
    },
    onUpdate: ({ editor }) => {
      if (onUpdate) {
        const html = editor.getHTML();
        const cleanedHtml = cleanEditorHTML(html);
        onUpdate(cleanedHtml);
      }
    },
  });

  // Update editor editable state when editable prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editable, editor]);

  // Update editor content when content prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Update editor state on changes
  useEffect(() => {
    if (!editor) return;

    const updateState = () => {
      // Check if editor view is available before accessing it
      if (!editor.view) return;

      try {
        setEditorState({
          isBold: editor.isActive("bold"),
          canBold: editor.can().toggleBold(),
          isItalic: editor.isActive("italic"),
          canItalic: editor.can().toggleItalic(),
          isStrike: editor.isActive("strike"),
          canStrike: editor.can().toggleStrike(),
          isUnderline: editor.isActive("underline"),
          canUnderline: editor.can().toggleUnderline(),
          isCode: editor.isActive("code"),
          canCode: editor.can().toggleCode(),
          isParagraph: editor.isActive("paragraph"),
          isHeading1: editor.isActive("heading", { level: 1 }),
          isHeading2: editor.isActive("heading", { level: 2 }),
          isHeading3: editor.isActive("heading", { level: 3 }),
          isHeading4: editor.isActive("heading", { level: 4 }),
          isBulletList: editor.isActive("bulletList"),
          isOrderedList: editor.isActive("orderedList"),
          isCodeBlock: editor.isActive("codeBlock"),
          isBlockquote: editor.isActive("blockquote"),
          alignLeft: editor.isActive({ textAlign: "left" }),
          alignCenter: editor.isActive({ textAlign: "center" }),
          alignRight: editor.isActive({ textAlign: "right" }),
          alignJustify: editor.isActive({ textAlign: "justify" }),
          isLink: editor.isActive("link"),
          isSubscript: editor.isActive("subscript"),
          isSuperscript: editor.isActive("superscript"),
          canUndo: editor.can().undo(),
          canRedo: editor.can().redo(),
        });
      } catch (error) {
        // Silently handle errors when editor view is not ready
        console.debug("Editor view not ready:", error);
      }
    };

    editor.on("selectionUpdate", updateState);
    editor.on("update", updateState);
    updateState();

    return () => {
      editor.off("selectionUpdate", updateState);
      editor.off("update", updateState);
    };
  }, [editor]);

  // Get current heading/paragraph type
  const getCurrentHeadingLabel = () => {
    if (editorState?.isHeading1) return "H1";
    if (editorState?.isHeading2) return "H2";
    if (editorState?.isHeading3) return "H3";
    if (editorState?.isHeading4) return "H4";
    return "P";
  };

  // Handle link insertion
  const handleLink = () => {
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    const previousUrl = editor.getAttributes("link").href;

    // If there's already a link, populate the form with existing values
    if (previousUrl) {
      setLinkText(selectedText || "");
      setLinkUrl(previousUrl);
    } else {
      // Use selected text or empty string
      setLinkText(selectedText || "");
      setLinkUrl("");
    }

    setIsLinkModalOpen(true);
  };

  const handleLinkSubmit = () => {
    if (!linkUrl.trim()) {
      return;
    }

    // If there's link text provided, insert it as a link
    if (linkText.trim()) {
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${linkUrl}">${linkText}</a>`)
        .run();
    } else {
      // If no text provided, set link on current selection or insert URL as text
      const { from, to } = editor.state.selection;
      if (from !== to) {
        // There's a selection, make it a link
        editor.chain().focus().setLink({ href: linkUrl }).run();
      } else {
        // No selection, insert URL as clickable link
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${linkUrl}">${linkUrl}</a>`)
          .run();
      }
    }

    setIsLinkModalOpen(false);
    setLinkText("");
    setLinkUrl("");
  };

  const handleRemoveLink = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setIsLinkModalOpen(false);
    setLinkText("");
    setLinkUrl("");
  };

  const handleAddImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl, alignment: imageAlignment, fileName: null }).run();
      setImageUrl("");
      setImageFile(null);
      setImagePreview(null);
      setImagePreviewError(false);
      setImageAlignment("center");
      setShowImageModal(false);
    } else if (imageFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        editor.chain().focus().setImage({ src: e.target.result, alignment: imageAlignment, fileName: imageFile.name }).run();
        setImageUrl("");
        setImageFile(null);
        setImagePreview(null);
        setImagePreviewError(false);
        setImageAlignment("center");
        setShowImageModal(false);
      };
      reader.readAsDataURL(imageFile);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      setImageUrl(""); 
      setImagePreviewError(false);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (url) => {
    setImageUrl(url);
    setImageFile(null);
    setImagePreview(null);
    setImagePreviewError(false);
  };

  const handleEditImage = (imageAttrs) => {
    setSelectedImageNode(imageAttrs);
    const src = imageAttrs.src || '';
    const alignment = imageAttrs.alignment || 'center';
    const fileName = imageAttrs.fileName || null;
    
    if (src.startsWith('data:image/')) {
      setEditingImageUrl('');
      setEditingImageFile(null);
      const fakeFile = {
        name: fileName || 'Uploaded Image',
        type: 'image/png',
        size: 0,
        dataUrl: src
      };
      setEditingImageFile(fakeFile);
      // Set preview for edit mode - it will be shown in upload tab
      setEditingImagePreview(src);
    } else {
      // It's a regular URL
      setEditingImageUrl(src);
      setEditingImageFile(null);
      // Set preview for edit mode - it will be shown in URL tab
      setEditingImagePreview(src);
    }
    
    setEditingImagePreviewError(false);
    setEditingImageAlignment(alignment);
    setShowEditImageModal(true);
  };

  const handleUpdateImage = () => {
    if (editingImageUrl) {
      if (selectedImageNode) {
        editor.chain().focus().updateAttributes('image', { src: editingImageUrl, alignment: editingImageAlignment, fileName: null }).run();
      }
      setEditingImageUrl("");
      setEditingImageFile(null);
      setEditingImagePreview(null);
      setEditingImagePreviewError(false);
      setEditingImageAlignment("center");
      setShowEditImageModal(false);
      setSelectedImageNode(null);
    } else if (editingImageFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (selectedImageNode) {
          const fileName = editingImageFile.name || selectedImageNode.fileName || null;
          editor.chain().focus().updateAttributes('image', { src: e.target.result, alignment: editingImageAlignment, fileName: fileName }).run();
        }
        setEditingImageUrl("");
        setEditingImageFile(null);
        setEditingImagePreview(null);
        setEditingImagePreviewError(false);
        setEditingImageAlignment("center");
        setShowEditImageModal(false);
        setSelectedImageNode(null);
      };
      if (editingImageFile.dataUrl) {
        // It's already a data URL, use it directly
        const fileName = editingImageFile.name || selectedImageNode?.fileName || null;
        editor.chain().focus().updateAttributes('image', { src: editingImageFile.dataUrl, alignment: editingImageAlignment, fileName: fileName }).run();
        setEditingImageUrl("");
        setEditingImageFile(null);
        setEditingImagePreview(null);
        setEditingImagePreviewError(false);
        setEditingImageAlignment("center");
        setShowEditImageModal(false);
        setSelectedImageNode(null);
      } else {
        reader.readAsDataURL(editingImageFile);
      }
    }
  };

  const handleEditFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setEditingImageFile(file);
      setEditingImageUrl(""); 
      setEditingImagePreviewError(false);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        // Only set preview when a new file is uploaded
        setEditingImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditImageUrlChange = (url) => {
    setEditingImageUrl(url);
    setEditingImageFile(null);
    // Clear preview when URL changes - it will be set when image loads successfully
    if (!url) {
      setEditingImagePreview(null);
    }
    setEditingImagePreviewError(false);
  };

  useEffect(() => {
    window.editImage = (imageAttrs) => {
      handleEditImage(imageAttrs);
    };
    
    return () => {
      delete window.editImage;
    };
  }, [editor]);

  // Toolbar button component
  const ToolbarButton = ({
    onClick,
    isActive = false,
    disabled = false,
    children,
    title,
    className = "",
  }) => {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        data-title={title}
        className={`p-2 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-opacity-80 ${className}`}
        style={{
          backgroundColor: isActive
            ? theme.palette.info?.main || "#3b82f6"
            : "transparent",
          color: isActive
            ? theme.palette.text?.light || "#ffffff"
            : theme.palette.text?.main || "#374151",
        }}
      >
        {children}
      </button>
    );
  };

  // Separator component
  const Separator = () => (
    <div
      className="w-px h-6 mx-1"
      style={{ backgroundColor: theme.palette.secondary?.v4 || "#e5e7eb" }}
    />
  );

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 mx-auto"></div>
          <p className="mt-4">Loading editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full rounded border shadow-sm bg-white">
      {/* Toolbar */}
      {showToolbar && editable && (
        <div
          className="flex items-center gap-1 px-2 py-2 flex-wrap border-b rounded-t-lg sticky top-0 z-10"
          style={{
            backgroundColor: theme.palette.primary?.main || "#ffffff",
            borderColor: theme.palette.secondary?.v4 || "#e5e7eb",
          }}
        >
          {/* Undo/Redo */}
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editorState?.canUndo}
            title="Undo"
          >
            <Undo size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editorState?.canRedo}
            title="Redo"
          >
            <Redo size={16} />
          </ToolbarButton>

          <Separator />

          {/* Heading/Paragraph Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowHeadingMenu(!showHeadingMenu)}
              title="Select the heading level"
              className="px-2 py-2 rounded flex items-center gap-1 hover:bg-opacity-80 transition-colors"
              style={{
                backgroundColor:
                  editorState?.isParagraph ||
                  editorState?.isHeading1 ||
                  editorState?.isHeading2 ||
                  editorState?.isHeading3 ||
                  editorState?.isHeading4
                    ? theme.palette.info?.main || "#3b82f6"
                    : "transparent",
                color:
                  editorState?.isParagraph ||
                  editorState?.isHeading1 ||
                  editorState?.isHeading2 ||
                  editorState?.isHeading3 ||
                  editorState?.isHeading4
                    ? theme.palette.text?.light || "#ffffff"
                    : theme.palette.text?.main || "#374151",
              }}
            >
              <span className="text-sm font-medium">
                {getCurrentHeadingLabel()}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            {showHeadingMenu && (
              <>
                <div
                  className="absolute top-full left-0 mt-1 py-1 rounded shadow-lg z-50 border"
                  style={{
                    backgroundColor: theme.palette.primary?.main || "#ffffff",
                    borderColor: theme.palette.secondary?.v4 || "#e5e7eb",
                  }}
                >
                  <button
                    onClick={() => {
                      editor.chain().focus().setParagraph().run();
                      setShowHeadingMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-opacity-50 text-sm"
                    style={{
                      backgroundColor: editorState?.isParagraph
                        ? theme.palette.info?.main || "#3b82f6"
                        : "transparent",
                      color: editorState?.isParagraph
                        ? theme.palette.text?.light || "#ffffff"
                        : theme.palette.text?.main || "#374151",
                    }}
                  >
                    Paragraph
                  </button>
                  {[1, 2, 3, 4].map((level) => (
                    <button
                      key={level}
                      onClick={() => {
                        editor.chain().focus().toggleHeading({ level }).run();
                        setShowHeadingMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-opacity-50 text-sm"
                      style={{
                        backgroundColor: editorState?.[`isHeading${level}`]
                          ? theme.palette.info?.main || "#3b82f6"
                          : "transparent",
                        color: editorState?.[`isHeading${level}`]
                          ? theme.palette.text?.light || "#ffffff"
                          : theme.palette.text?.main || "#374151",
                      }}
                    >
                      Heading {level}
                    </button>
                  ))}
                </div>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowHeadingMenu(false)}
                />
              </>
            )}
          </div>

          <Separator />

          {/* Lists */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editorState?.isBulletList}
            title="Bullet List"
          >
            <List size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editorState?.isOrderedList}
            title="Numbered List"
          >
            <ListOrdered size={16} />
          </ToolbarButton>

          <Separator />

          {/* Text Alignment */}
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            isActive={editorState?.alignLeft}
            title="Align Left"
          >
            <AlignLeft size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            isActive={editorState?.alignCenter}
            title="Align Center"
          >
            <AlignCenter size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            isActive={editorState?.alignRight}
            title="Align Right"
          >
            <AlignRight size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
            isActive={editorState?.alignJustify}
            title="Justify"
          >
            <AlignJustify size={16} />
          </ToolbarButton>

          <Separator />

          {/* Text Formatting */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editorState?.isBold}
            disabled={!editorState?.canBold}
            title="Bold"
          >
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editorState?.isItalic}
            disabled={!editorState?.canItalic}
            title="Italic"
          >
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editorState?.isStrike}
            disabled={!editorState?.canStrike}
            title="Strikethrough"
          >
            <Strikethrough size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editorState?.isUnderline}
            disabled={!editorState?.canUnderline}
            title="Underline"
          >
            <UnderlineIcon size={16} />
          </ToolbarButton>

          {/* Code & Block Elements */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editorState?.isCodeBlock}
            title="Code Block"
          >
            <SquareCode size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCode().run()}
            isActive={editorState?.isCode}
            disabled={!editorState?.canCode}
            title="Inline Code"
          >
            <Code2 size={16} />
          </ToolbarButton>

          <Separator />
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editorState?.isBlockquote}
            title="Blockquote"
          >
            <Quote size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Horizontal Rule"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M3 12h18M3 12l3-3m-3 3l3 3m15-3l-3-3m3 3l-3 3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            onClick={handleLink}
            isActive={editorState?.isLink}
            title="Link"
          >
            <LinkIcon size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            isActive={editorState?.isSuperscript}
            title="Superscript"
          >
            <SuperscriptIcon size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleSubscript().run()}
            isActive={editorState?.isSubscript}
            title="Subscript"
          >
            <SubscriptIcon size={16} />
          </ToolbarButton>

          <Separator />

          {/* Add Image */}
          <ToolbarButton
            onClick={() => {
              setShowImageModal(true);
              setImagePreview(null);
              setImagePreviewError(false);
            }}
            title="Add Image"
          >
            <ImageIcon size={16} />
          </ToolbarButton>
        </div>
      )}

      {/* Editor Content */}
      <div className={`flex-1 overflow-auto rounded`}>
        <div
          className="h-full"
          style={{
            backgroundColor: theme.palette.primary?.main || "#ffffff",
            minHeight: "400px",
          }}
        >
          <EditorContent
            editor={editor}
            style={{
              color: theme.palette.text?.main || "#374151",
            }}
          />
        </div>
      </div>

      {/* Link Modal */}
      {isLinkModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-70"
          onClick={handleLinkCancel}
        >
          <div
            ref={linkModalRef}
            className="bg-white rounded-lg w-full max-w-md mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="link-modal-title"
          >
            {/* Modal Header */}
            <div
              className="flex justify-between items-center px-5 py-4 border-b"
              style={{ borderColor: theme.palette.secondary?.v4 || "#e5e7eb" }}
            >
              <h2 id="link-modal-title" className="font-semibold text-lg">
                Add Link
              </h2>
              <button
                onClick={handleLinkCancel}
                aria-label="Close modal"
                className="bg-black text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-gray-800 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-5 py-4 space-y-4">
              {/* Link Text Input */}
              <div>
                <label
                  htmlFor="link-text"
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.palette.text?.main || "#374151" }}
                >
                  Link Text
                </label>
                <input
                  id="link-text"
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{
                    borderColor: theme.palette.secondary?.v4 || "#e5e7eb",
                    color: theme.palette.text?.main || "#374151",
                  }}
                  placeholder="Enter link text"
                  autoFocus
                />
              </div>

              {/* URL Input */}
              <div>
                <label
                  htmlFor="link-url"
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.palette.text?.main || "#374151" }}
                >
                  URL
                </label>
                <input
                  id="link-url"
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{
                    borderColor: theme.palette.secondary?.v4 || "#e5e7eb",
                    color: theme.palette.text?.main || "#374151",
                  }}
                  placeholder="https://example.com"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && linkUrl.trim()) {
                      handleLinkSubmit();
                    }
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="flex justify-end gap-3 px-5 py-4 border-t"
              style={{ borderColor: theme.palette.secondary?.v4 || "#e5e7eb" }}
            >
              <button
                onClick={handleLinkCancel}
                className="px-4 py-2 rounded-md border transition-colors"
                style={{
                  borderColor: theme.palette.info?.main || "#3b82f6",
                  color: theme.palette.info?.main || "#3b82f6",
                  backgroundColor: "transparent",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLinkSubmit}
                disabled={!linkUrl.trim()}
                className="px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: linkUrl.trim()
                    ? theme.palette.info?.main || "#3b82f6"
                    : theme.palette.secondary?.v3 || "#d1d5db",
                  color: "#ffffff",
                }}
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      <ImageModal
        isOpen={showImageModal || showEditImageModal}
        onClose={() => {
          if (showImageModal) {
            setShowImageModal(false);
            setImageUrl("");
            setImageFile(null);
            setImagePreview(null);
            setImagePreviewError(false);
            setImageAlignment("center");
          }
          if (showEditImageModal) {
            setShowEditImageModal(false);
            setEditingImageUrl("");
            setEditingImageFile(null);
            setEditingImagePreview(null);
            setEditingImagePreviewError(false);
            setEditingImageAlignment("center");
            setSelectedImageNode(null);
          }
        }}
        onSubmit={showEditImageModal ? handleUpdateImage : handleAddImage}
        mode={showEditImageModal ? "edit" : "add"}
        imageUrl={showEditImageModal ? editingImageUrl : imageUrl}
        setImageUrl={showEditImageModal ? setEditingImageUrl : setImageUrl}
        imageFile={showEditImageModal ? editingImageFile : imageFile}
        setImageFile={showEditImageModal ? setEditingImageFile : setImageFile}
        imagePreview={showEditImageModal ? editingImagePreview : imagePreview}
        setImagePreview={showEditImageModal ? setEditingImagePreview : setImagePreview}
        imagePreviewError={showEditImageModal ? editingImagePreviewError : imagePreviewError}
        setImagePreviewError={showEditImageModal ? setEditingImagePreviewError : setImagePreviewError}
        handleFileChange={showEditImageModal ? handleEditFileChange : handleFileChange}
        handleImageUrlChange={showEditImageModal ? handleEditImageUrlChange : handleImageUrlChange}
        imageAlignment={showEditImageModal ? editingImageAlignment : imageAlignment}
        setImageAlignment={showEditImageModal ? setEditingImageAlignment : setImageAlignment}
      />
    </div>
  );
};

// Export methods for external use
TiptapEditor.getContent = (editor) => {
  return editor ? editor.getHTML() : "";
};

TiptapEditor.setContent = (editor, html) => {
  if (editor) {
    editor.commands.setContent(html);
  }
};

export default TiptapEditor;
